import { ref, watch } from "vue";
import { toBoolean } from "#ce/utils/props";

/**
 * Open-state bridge for components that wrap `sp-dialog` (sp-modal,
 * sp-bottom-sheet). Owns the internal `open` ref, mirrors the external `open`
 * prop into it (coercing attribute strings), re-emits `update-open` on every
 * change, and syncs the ref back from the inner dialog's `update-open` event.
 *
 * Close emission is the caller's responsibility: both wrappers emit `close` from
 * the inner dialog's own `close` event (a bare `update-open(false)` does not
 * close them), matching sp-dialog. The bridge never emits `close` itself.
 *
 * @param {Object} props - The wrapper props; reads `props.open`.
 * @param {(event: string, ...args: unknown[]) => void} emit - The component emit.
 * @returns {{
 *   open: import("vue").Ref<boolean>,
 *   handleDialogUpdateOpen: (event: CustomEvent) => void,
 * }}
 */
export function useDialogBridge(props, emit) {
  const open = ref(toBoolean(props.open));

  function handlePropOpenChange(value) {
    open.value = toBoolean(value);
  }

  function handleOpenChange(value) {
    emit("update-open", value);
  }

  /**
   * Sync the internal open ref from the inner dialog's `update-open` event.
   *
   * @param {CustomEvent} event - The inner dialog `update-open` event; its
   *   `detail` is the emit-args array `[value]`.
   */
  function handleDialogUpdateOpen({ detail }) {
    const [value] = detail;
    open.value = value;
  }

  watch(() => props.open, handlePropOpenChange);
  watch(open, handleOpenChange);

  return { open, handleDialogUpdateOpen };
}
